import React, { useState, useEffect, useCallback } from 'react';
import { getHistory } from '../../utils/api';
import Pagination from '../Pagination';

const PER_PAGE = 15;

const History = ({ account, f, fBC, vBC, BC, SYM }) => {
  const tr = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '—';

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (pg) => {
    if (!account) return;
    setLoading(true);
    try {
      const res = await getHistory(account, undefined, pg, PER_PAGE);
      setRows(res.transactions || []);
      setPages(res.pagination?.pages || 1);
      setTotal(res.pagination?.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [account]);

  useEffect(() => { load(page); }, [load, page]);

  const typeLabel = (type) => {
    const labels = {
      'buy': 'Buy',
      'sell': 'Sell',
      'level_income_claim': 'Claim',
      'p2p_transfer': 'P2P Sent',
      'p2p_receive': 'P2P Received',
    };
    return labels[type] || type;
  };

  const typeClass = (type) => {
    if (type === 'buy' || type === 'p2p_receive') return 'buy';
    if (type === 'sell') return 'sell';
    if (type === 'p2p_transfer') return 'p2p';
    return 'claim';
  };

  return (
    <div className="hist-root fade-in">
      <style>{`
        .hist-root .hist-date { line-height: 1.35; }
        .hist-root .hist-date-d { display: block; white-space: nowrap; }
        .hist-root .hist-date-t { display: block; font-size: 11px; color: var(--text-3); white-space: nowrap; }
        @media (max-width: 768px) {
          .hist-root .table th,
          .hist-root .table td { padding: 8px 6px; font-size: 12px; }
          .hist-root .table .mono { font-size: 11px; }
          .hist-root .hist-date-d { font-size: 12px; }
          .hist-root .hist-date-t { font-size: 10px; }
        }
      `}</style>
      <h3>Transaction History</h3>
      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : rows.length > 0 ? (
        <>
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Date</th><th>Type</th><th>Details</th><th>Tokens</th><th>Value</th><th>Price</th></tr></thead>
          <tbody>{rows.map(tx => (
            <tr key={tx._id}>
              <td className="hist-date">
                <span className="hist-date-d">{new Date(tx.createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                <span className="hist-date-t">{new Date(tx.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
              </td>
              <td><span className={`d-tx-badge ${typeClass(tx.type)}`}>{typeLabel(tx.type)}</span></td>
              <td className="mono" style={{ fontSize: 12 }}>
                {tx.type === 'buy' && `Paid ${fBC(tx.bnbAmount, 4)} ${BC}`}
                {tx.type === 'sell' && (
                  <>
                    Received {fBC(tx.bnbReceived, 4)} {BC}
                    {tx.tokensBurned > 0 && <span className="text-3"> ({f(tx.tokensBurned, 2)} burned)</span>}
                  </>
                )}
                {tx.type === 'level_income_claim' && `Claimed ${fBC(tx.claimAmount, 4)} ${BC}`}
                {tx.type === 'p2p_transfer' && (
                  <>
                    To: <strong>{tx.p2pReceiverRoboId || tr(tx.p2pReceiverWallet)}</strong>
                    <br />
                    Sent {f(tx.tokensSold, 2)} → Received {f(tx.p2pTokensReceived, 2)}
                    {tx.tokensBurned > 0 && <span className="text-3"> ({f(tx.tokensBurned, 2)} burned)</span>}
                  </>
                )}
                {tx.type === 'p2p_receive' && (
                  <>
                    From: <strong>{tx.p2pReceiverRoboId || tr(tx.p2pReceiverWallet)}</strong>
                    <br />
                    Received {f(tx.tokensToUser, 2)} {SYM}
                  </>
                )}
              </td>
              <td className="mono">
                {tx.type === 'buy' && <span className="text-green">+{f(tx.tokensToUser, 2)}</span>}
                {tx.type === 'sell' && <span className="text-red">-{f(tx.tokensSold, 2)}</span>}
                {tx.type === 'level_income_claim' && '—'}
                {tx.type === 'p2p_transfer' && <span className="text-red">-{f(tx.tokensSold, 2)}</span>}
                {tx.type === 'p2p_receive' && <span className="text-green">+{f(tx.tokensToUser, 2)}</span>}
              </td>
              <td className="mono">
                {tx.type === 'buy' && vBC(tx.bnbAmount, 4)}
                {tx.type === 'sell' && vBC(tx.bnbReceived, 4)}
                {tx.type === 'level_income_claim' && vBC(tx.claimAmount, 4)}
                {tx.type === 'p2p_transfer' && vBC((tx.tokensSold || 0) * (tx.tokenPrice || 0), 4)}
                {tx.type === 'p2p_receive' && vBC((tx.tokensToUser || 0) * (tx.tokenPrice || 0), 4)}
              </td>
              <td className="mono">{f(tx.tokenPrice, 6)}</td>
            </tr>
          ))}</tbody>
        </table></div>
        <Pagination page={page} pages={pages} total={total} perPage={PER_PAGE} onPage={setPage} />
        </>
      ) : <div className="d-empty">No transactions yet.</div>}
    </div>
  );
};

export default History;
